// src/components/inventory/SupplierFormModal.js
//
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • Lead time (and payment terms / website / notes when the suppliers table has those columns —
//     read from GET /purchase-orders/suppliers/meta). The lead time feeds the FlowSense re-order
//     point; the lead time FlowSense learned from delivered orders is shown with "Use it".
//   • Only the supplier's editable columns are sent (the edit form posted the whole row back,
//     including ids and timestamps).
//   • Validation: name required and unique (case-insensitive), e-mail format, phone characters,
//     lead time 0–365; the server's reason is shown when a save fails (was "Execution Error").
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, Button, FormControl, FormLabel,
  Input, VStack, useToast, useColorModeValue, SimpleGrid, Icon, Text,
  HStack // Added missing import
} from '@chakra-ui/react';
import { FormErrorMessage, FormHelperText, NumberInput, NumberInputField, Textarea } from '@chakra-ui/react'; // --- UPDATE
import { MdBusinessCenter } from 'react-icons/md';
import { MdAutoAwesome } from 'react-icons/md'; // --- UPDATE
import supplierService from '../../services/supplierService';
import purchaseOrderService, { poErrorMessage } from '../../services/purchaseOrderService'; // --- UPDATE

// --- UPDATE
const BASE_FIELDS = ['name', 'contact_person', 'email', 'phone', 'address'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+()\-.\s/ext]*$/i;
// --- END UPDATE

const SupplierFormModal = ({ isOpen, onClose, supplier, onSave, existingNames = [], score = null }) => { // --- UPDATE: + existingNames, score (FlowSense record)
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meta, setMeta] = useState(null); // --- UPDATE
  const toast = useToast();

  const modalBg = useColorModeValue("white", "gray.800");
  const inputBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const aiColor = useColorModeValue("teal.600", "teal.300"); // --- UPDATE

  useEffect(() => {
    if (supplier) setFormData({ ...supplier }); // --- UPDATE: copy
    else setFormData({ name: '', contact_person: '', email: '', phone: '', address: '' });
  }, [supplier, isOpen]);

  // --- UPDATE: which columns this database has
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    purchaseOrderService.getSupplierMeta().then((m) => { if (alive) setMeta(m); }).catch(() => { if (alive) setMeta(null); });
    return () => { alive = false; };
  }, [isOpen]);
  const editable = meta?.editable?.length ? meta.editable : BASE_FIELDS;
  const leadCol = meta?.lead_time_column || null;
  const has = (k) => editable.includes(k);
  const set = (k) => (e) => setFormData((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const name = String(formData.name || '').trim();
  const errors = {
    name: !name ? null : existingNames.some((n) => String(n || '').trim().toLowerCase() === name.toLowerCase()) ? 'A supplier with this name already exists.' : name.length > 100 ? 'Use 100 characters or fewer.' : null,
    email: formData.email && !EMAIL_RE.test(String(formData.email).trim()) ? 'Enter a valid e-mail address.' : null,
    phone: formData.phone && !PHONE_RE.test(String(formData.phone)) ? 'Use digits, spaces, + ( ) - only.' : null,
    lead: leadCol && formData[leadCol] !== undefined && formData[leadCol] !== '' && formData[leadCol] !== null && (Number(formData[leadCol]) < 0 || Number(formData[leadCol]) > 365 || !Number.isFinite(Number(formData[leadCol]))) ? 'Lead time must be 0–365 days.' : null,
  };
  const invalid = !name || Object.values(errors).some(Boolean);
  // --- END UPDATE

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalid) return; // --- UPDATE
    setIsSubmitting(true);
    // --- UPDATE: only the editable columns, trimmed; empty optional values become null
    const body = Object.fromEntries(editable.filter((k) => formData[k] !== undefined).map((k) => {
      const v = formData[k];
      if (k === leadCol || k === 'lead_time_days' || k === 'average_lead_time_days') return [k, v === '' || v === null ? null : Number(v)];
      return [k, typeof v === 'string' ? (v.trim() === '' && k !== 'name' ? null : v.trim()) : v];
    }));
    // --- END UPDATE
    try {
      if (supplier) await supplierService.updateSupplier(supplier.id, body); // --- UPDATE: body (was the whole row)
      else await supplierService.createSupplier(body);

      toast({ title: 'System Updated', status: 'success' });
      onSave();
      onClose();
    } catch (error) {
      toast({ title: 'Execution Error', description: poErrorMessage(error), status: 'error' }); // --- UPDATE: server reason
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg} borderRadius="2xl">
        <ModalHeader borderBottomWidth="1px">
          <HStack>
            <Icon as={MdBusinessCenter} color="purple.500" />
            <Text>{supplier ? 'Edit Partner Profile' : 'Register New Partner'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <VStack spacing={6}>
            <FormControl isRequired isInvalid={!!errors.name}>
              <FormLabel fontWeight="bold">Company Legal Name</FormLabel>
              <Input
                bg={inputBg}
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                maxLength={120}
                aria-label="Company legal name"
              />
              {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}{/* --- UPDATE */}
            </FormControl>

            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl>
                <FormLabel fontWeight="bold">Account Manager</FormLabel>
                <Input bg={inputBg} value={formData.contact_person || ''}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})} />
              </FormControl>
              <FormControl isInvalid={!!errors.phone}>
                <FormLabel fontWeight="bold">Direct Phone</FormLabel>
                <Input bg={inputBg} value={formData.phone || ''}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})} aria-label="Direct phone" />
                {errors.phone && <FormErrorMessage>{errors.phone}</FormErrorMessage>}{/* --- UPDATE */}
              </FormControl>
            </SimpleGrid>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontWeight="bold">Enterprise Email</FormLabel>
              <Input type="email" bg={inputBg} value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})} aria-label="Enterprise email" />
              {errors.email && <FormErrorMessage>{errors.email}</FormErrorMessage>}{/* --- UPDATE */}
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold">Headquarters Address</FormLabel>
              <Input bg={inputBg} value={formData.address || ''}
                onChange={(e) => setFormData({...formData, address: e.target.value})} />
            </FormControl>

            {/* --- UPDATE (Procurement MOW+ v1): fields this database has */}
            {(leadCol || has('payment_terms')) && (
              <SimpleGrid columns={2} spacing={4} w="full">
                {leadCol && (
                  <FormControl isInvalid={!!errors.lead}>
                    <FormLabel fontWeight="bold">Lead time (days)</FormLabel>
                    <NumberInput min={0} max={365} value={formData[leadCol] ?? ''} onChange={(v) => setFormData((f) => ({ ...f, [leadCol]: v }))}><NumberInputField bg={inputBg} aria-label="Lead time days" /></NumberInput>
                    {errors.lead ? <FormErrorMessage>{errors.lead}</FormErrorMessage> : <FormHelperText fontSize="xs">Used in the FlowSense re-order point.</FormHelperText>}
                    {score?.learned_lead_time_days != null && Number(formData[leadCol]) !== score.learned_lead_time_days && (
                      <HStack mt={1} spacing={1}><Icon as={MdAutoAwesome} color={aiColor} /><Text fontSize="xs" color={aiColor}>Real lead time from deliveries: {score.learned_lead_time_days} days</Text>
                        <Button size="xs" variant="link" colorScheme="teal" onClick={() => setFormData((f) => ({ ...f, [leadCol]: score.learned_lead_time_days }))}>Use it</Button></HStack>
                    )}
                  </FormControl>
                )}
                {has('payment_terms') && <FormControl><FormLabel fontWeight="bold">Payment terms</FormLabel><Input bg={inputBg} value={formData.payment_terms || ''} onChange={set('payment_terms')} placeholder="e.g. Net 30" /></FormControl>}
              </SimpleGrid>
            )}
            {has('website') && <FormControl><FormLabel fontWeight="bold">Website</FormLabel><Input bg={inputBg} value={formData.website || ''} onChange={set('website')} placeholder="https://" /></FormControl>}
            {has('notes') && <FormControl><FormLabel fontWeight="bold">Notes</FormLabel><Textarea bg={inputBg} value={formData.notes || ''} onChange={set('notes')} rows={2} /></FormControl>}
            {/* --- END UPDATE */}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Discard</Button>
          <Button colorScheme="purple" type="submit" isLoading={isSubmitting} isDisabled={invalid}>Synchronize</Button>{/* --- UPDATE: disabled until valid */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SupplierFormModal;
